import {
  useState,
} from 'react';
import {
  AccentPicker,
  Callout,
  Field,
  Modal,
  TextInput,
  explainFailure,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import { t } from '../../messages.ts';

/**
 * §12.4 — a brand's own accent colour, set directly.
 *
 * Everything else on a brand's own popup is either read-only here or a
 * proposal (`ProposeBrandChange`) the operator's own people approve, because
 * §3.9d reserves the operator's approval for its commercial terms. A colour
 * is not one — it changes nothing an affiliate is owed or a figure anyone
 * reads — so this writes straight into that operator's own row, the same way
 * `setOperatorCountry` and `assignPlan` already do.
 */
export function SetBrandColourModal({
  api,
  operatorId,
  brand,
  onClose,
  onSaved,
}: {
  api: PlatformApi;
  operatorId: string;
  brand: { brand_id: string; display_name: string; theme_accent: string | null };
  onClose: () => void;
  onSaved: (result: { brand_id: string; theme_accent: string | null }) => void;
}) {
  const [colour, setColour] = useState<string | null>(brand.theme_accent);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  return (
    <Modal title={t('brand.colour.title', { name: brand.display_name })} onClose={onClose}>
      <div className="form">
        {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
        <AccentPicker value={colour} onChange={setColour} disabled={busy} />
        <Field label={t('brand.colour.why')} hint={t('brand.colour.whyHint')}>
          <TextInput value={reason} onChange={setReason} placeholder={t('brand.colour.optional')} />
        </Field>
        <div className="form-actions">
          <button type="button" className="copy" disabled={busy} onClick={onClose}>{t('common.cancel')}</button>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setFailure(null);
              api
                .setBrandTheme(operatorId, brand.brand_id, {
                  theme_accent: colour,
                  ...(reason.trim() ? { reason: reason.trim() } : {}),
                })
                .then(onSaved)
                .catch((e: unknown) => setFailure(e))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? t('brand.colour.saving') : t('brand.colour.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
